/**
 * Admin Cascade Contracts - Database-First Zod Schemas
 *
 * SOURCE OF TRUTH: PostgreSQL tables and stored procedures
 * Generated from MCP queries on 2026-01-02
 *
 * Related DB objects:
 * - Table: payment_services (id, hash, name, type, turned_off, restarted)
 * - Table: cascade_new (id, deposits jsonb, withdrawals jsonb, started_at, ended_at)
 * - Table: cascade_config_new (cascade_id, deposit_positions jsonb, withdrawal_positions jsonb)
 * - Table: payment_service_config (payment_service_id, min_amount, max_amount)
 * - Function: enable_cascade_new(p_deposit_hashes jsonb, p_withdrawal_hashes jsonb) -> integer
 * - Function: disable_cascade_new() -> void
 * - Function: get_active_cascade_new() -> TABLE(id, deposits, withdrawals, started_at)
 * - Function: set_method_position_new(p_hash, p_position, p_type, p_staff_id) -> jsonb
 * - Function: get_cascade_info_new(p_amount, p_type) -> jsonb
 * - Function: get_current_cascade_config_new(p_cascade_id) -> TABLE(...)
 *
 * Constraint: valid_type CHECK (type IN ('deposit', 'withdrawal'))
 */
import { z } from 'zod';
import { paymentServiceTypeSchema } from '../internal/cascade.js';
// Re-export for convenience (already exported from internal)
export { paymentServiceTypeSchema };
// =============================================================================
// COMMON SCHEMAS
// =============================================================================
/**
 * Cascade position item (method in cascade)
 * Used in cascade configuration responses
 */
export const cascadePositionItemSchema = z.object({
    position: z.number().int().min(0).max(10),
    method: z.string(), // Legacy name (hash)
    hash: z.string(),
    displayName: z.string(),
    minAmount: z.number().int().nullable(),
    maxAmount: z.number().int().nullable(),
});
/**
 * Cascade position item V2 (includes active flag)
 */
export const cascadePositionItemV2Schema = z.object({
    hash: z.string(),
    name: z.string(),
    position: z.number().int().min(0).max(10),
    minAmount: z.number().int().nullable(),
    maxAmount: z.number().int().nullable(),
    active: z.boolean(),
});
/**
 * Disabled method (available for adding to cascade)
 */
export const disabledMethodSchema = z.object({
    method: z.string(), // Legacy: name
    hash: z.string(),
    displayName: z.string(),
    minAmount: z.number().int().nullable(),
    maxAmount: z.number().int().nullable(),
});
/**
 * Payment service entity (full details)
 */
export const paymentServiceSchema = z.object({
    id: z.number().int(),
    hash: z.string(),
    name: z.string(),
    type: paymentServiceTypeSchema,
    active: z.boolean(),
    minAmount: z.number().int().nullable(),
    maxAmount: z.number().int().nullable(),
});
// =============================================================================
// RESPONSE DATA TYPES (for frontend - inner data without success wrapper)
// =============================================================================
/**
 * Cascade config response data (inner data)
 * Used by frontend after extracting from API response
 */
export const cascadeConfigResponseDataSchema = z.object({
    enabled: z.boolean(),
    positions: z.array(cascadePositionItemSchema),
});
/**
 * Disabled methods response data (inner data)
 */
export const disabledMethodsResponseDataSchema = z.object({
    methods: z.array(disabledMethodSchema),
});
/**
 * Cascade stats item (per method)
 * Note: Backend uses 'successful', some frontend code may use 'success' - normalize in hook
 */
export const cascadeStatsItemSchema = z.object({
    paymentMethod: z.string(),
    skipped: z.number().int(),
    failed: z.number().int(),
    successful: z.number().int(),
    total: z.number().int(),
});
/**
 * Cascade stats response data (inner data)
 */
export const cascadeStatsResponseDataSchema = z.object({
    date: z.string(),
    stats: z.array(cascadeStatsItemSchema),
    note: z.string().optional(),
});
// =============================================================================
// DEPOSIT CASCADE - LEGACY ENDPOINTS
// =============================================================================
/**
 * GET /api/admin/cascade/deposit response
 */
export const getDepositCascadeResponseSchema = z.object({
    success: z.literal(true),
    data: cascadeConfigResponseDataSchema,
});
/**
 * POST /api/admin/cascade/deposit/toggle request
 */
export const toggleCascadeRequestSchema = z.object({
    enabled: z.boolean(),
});
/**
 * POST /api/admin/cascade/deposit/toggle response
 */
export const toggleCascadeResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        message: z.string(),
        enabled: z.boolean(),
    }),
});
/**
 * POST /api/admin/cascade/deposit/swap request
 * Supports both position-based (legacy) and hash-based (new) swapping
 */
export const swapPositionsRequestSchema = z.object({
    position1: z.number().int().min(1).max(10).optional(),
    position2: z.number().int().min(1).max(10).optional(),
    hash1: z.string().optional(),
    hash2: z.string().optional(),
}).refine((data) => (data.hash1 && data.hash2) || (data.position1 !== undefined && data.position2 !== undefined), { message: 'Either (hash1, hash2) or (position1, position2) must be provided' });
/**
 * POST /api/admin/cascade/deposit/swap response
 */
export const swapPositionsResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        message: z.string(),
    }),
});
/**
 * POST /api/admin/cascade/deposit/drop request
 */
export const dropFromCascadeRequestSchema = z.object({
    position: z.number().int().min(1).max(10).optional(),
    hash: z.string().optional(),
}).refine((data) => data.hash || data.position !== undefined, { message: 'Either hash or position must be provided' });
/**
 * POST /api/admin/cascade/deposit/drop response
 */
export const dropFromCascadeResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        message: z.string(),
    }),
});
/**
 * POST /api/admin/cascade/deposit/add request
 */
export const addToCascadeRequestSchema = z.object({
    method: z.string().optional(), // Legacy: method name
    hash: z.string().optional(),
    position: z.number().int().min(1).max(10),
}).refine((data) => data.hash || data.method, { message: 'Either hash or method name must be provided' });
/**
 * POST /api/admin/cascade/deposit/add response
 */
export const addToCascadeResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        message: z.string(),
    }),
});
/**
 * GET /api/admin/cascade/deposit/disabled response
 */
export const getDisabledMethodsResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        methods: z.array(disabledMethodSchema),
    }),
});
/**
 * POST /api/admin/cascade/deposit/reorder request
 */
export const reorderCascadeRequestSchema = z.object({
    order: z.array(z.string()).min(1), // Array of hashes or names
});
/**
 * POST /api/admin/cascade/deposit/reorder response
 */
export const reorderCascadeResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        message: z.string(),
        newOrder: z.array(z.string()),
    }),
});
// =============================================================================
// WITHDRAWAL CASCADE - LEGACY ENDPOINTS
// Same structure as deposit cascade
// =============================================================================
/**
 * GET /api/admin/cascade/withdrawal response
 * Note: Uses 'paymentMethod' instead of 'method' for withdrawal positions
 */
export const getWithdrawalCascadeResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        positions: z.array(z.object({
            position: z.number().int().min(0).max(10),
            paymentMethod: z.string(), // Different field name for withdrawals
            hash: z.string(),
            displayName: z.string(),
            minAmount: z.number().int().nullable(),
            maxAmount: z.number().int().nullable(),
        })),
    }),
});
// =============================================================================
// CASCADE V2 ENDPOINTS (hash-based)
// =============================================================================
/**
 * GET /api/admin/cascade/v2/deposit response
 */
export const getDepositCascadeV2ResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        enabled: z.boolean(),
        positions: z.array(cascadePositionItemV2Schema),
    }),
});
/**
 * POST /api/admin/cascade/v2/deposit/position request
 */
export const setPositionV2RequestSchema = z.object({
    hash: z.string().min(1),
    position: z.number().int().min(0).max(10), // 0 = disabled
});
/**
 * POST /api/admin/cascade/v2/deposit/position response
 */
export const setPositionV2ResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        message: z.string(),
        positions: z.unknown(), // JSONB from stored procedure
    }),
});
/**
 * GET /api/admin/cascade/v2/deposit/enabled response
 */
export const getEnabledMethodsV2ResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        methods: z.array(z.object({
            hash: z.string(),
            name: z.string(),
            position: z.number().int(),
            minAmount: z.number().int().nullable(),
            maxAmount: z.number().int().nullable(),
        })),
    }),
});
/**
 * GET /api/admin/cascade/v2/deposit/disabled response
 */
export const getDisabledMethodsV2ResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        methods: z.array(z.object({
            hash: z.string(),
            name: z.string(),
            minAmount: z.number().int().nullable(),
            maxAmount: z.number().int().nullable(),
        })),
    }),
});
/**
 * POST /api/admin/cascade/v2/toggle request/response
 */
export const toggleCascadeV2RequestSchema = z.object({
    enabled: z.boolean(),
});
export const toggleCascadeV2ResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        message: z.string(),
        cascadeId: z.number().int().optional(), // Present when enabling
    }),
});
/**
 * GET /api/admin/cascade/v2/services query params
 */
export const getPaymentServicesV2QuerySchema = z.object({
    type: paymentServiceTypeSchema.optional(),
});
/**
 * GET /api/admin/cascade/v2/services response
 */
export const getPaymentServicesV2ResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        services: z.array(paymentServiceSchema),
    }),
});
/**
 * GET /api/admin/cascade/v2/service/:hash response
 */
export const getPaymentServiceByHashV2ResponseSchema = z.object({
    success: z.literal(true),
    data: paymentServiceSchema,
});
// =============================================================================
// CASCADE STATS
// =============================================================================
/**
 * GET /api/admin/cascade/stats query params
 */
export const getCascadeStatsQuerySchema = z.object({
    date: z.string().optional(), // ISO date string
});
/**
 * GET /api/admin/cascade/stats response
 * Uses cascadeStatsResponseDataSchema defined above
 */
export const getCascadeStatsResponseSchema = z.object({
    success: z.literal(true),
    data: cascadeStatsResponseDataSchema,
});
// =============================================================================
// TOGGLE METHOD STATUS (Enable/Disable in cascade)
// =============================================================================
/**
 * POST /api/admin/cascade/deposit/:hash/toggle request
 * POST /api/admin/cascade/withdrawal/:hash/toggle request
 *
 * Toggles a payment method's cascade status:
 * - enabled=false: Sets position to 0 (disabled, skipped in cascade)
 * - enabled=true: Sets position to next available (enabled, included in cascade)
 */
export const toggleMethodStatusRequestSchema = z.object({
    enabled: z.boolean(),
});
/**
 * POST /api/admin/cascade/deposit/:hash/toggle response
 * POST /api/admin/cascade/withdrawal/:hash/toggle response
 */
export const toggleMethodStatusResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        hash: z.string(),
        enabled: z.boolean(),
        position: z.number().int().min(0),
        message: z.string(),
    }),
});
// =============================================================================
// ERROR RESPONSES
// =============================================================================
/**
 * Generic cascade error response
 */
export const cascadeErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
//# sourceMappingURL=cascade.js.map